import { useState, useEffect, useCallback } from 'react';
import { Store } from 'lucide-react';
import { useAuth } from '../hooks/useAuth.js';
import {
  browseMarketplace,
  subscribeToCatalog,
  listMySubscriptions,
  unsubscribeFromCatalog,
  ApiError,
} from '../api/client.js';
import type { PublishedCatalog, CatalogSubscription } from '../api/client.js';

export function MarketplacePage() {
  const { user } = useAuth();
  const [catalogs, setCatalogs] = useState<PublishedCatalog[]>([]);
  const [subscriptions, setSubscriptions] = useState<CatalogSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [subscribingId, setSubscribingId] = useState<string | null>(null);
  const [tab, setTab] = useState<'browse' | 'subscriptions'>('browse');
  const [unsubscribingId, setUnsubscribingId] = useState<string | null>(null);
  const [confirmUnsub, setConfirmUnsub] = useState<CatalogSubscription | null>(null);

  const loadData = useCallback(async () => {
    try {
      const [browseRes, subRes] = await Promise.all([
        browseMarketplace(),
        listMySubscriptions(),
      ]);
      setCatalogs(browseRes.catalogs);
      setSubscriptions(subRes.subscriptions);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load marketplace');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  const handleSubscribe = async (publishedCatalogId: string) => {
    setSubscribingId(publishedCatalogId);
    try {
      const res = await subscribeToCatalog(publishedCatalogId);
      showToast(`Subscribed! ${res.localCatalog.name} with ${res.productCount} products added to your catalogs.`);
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to subscribe');
    } finally {
      setSubscribingId(null);
    }
  };

  const handleUnsubscribe = async () => {
    if (!confirmUnsub) return;
    setUnsubscribingId(confirmUnsub.id);
    try {
      await unsubscribeFromCatalog(confirmUnsub.id);
      showToast('Unsubscribed successfully. Local catalog copy removed.');
      setConfirmUnsub(null);
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to unsubscribe');
    } finally {
      setUnsubscribingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-400 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-100">Marketplace</h1>
        <p className="mt-1 text-sm text-gray-400">
          Browse and subscribe to product catalogs from manufacturers
        </p>
      </div>

      {error && (
        <div className="mb-4 rounded-lg bg-red-900/30 px-4 py-3 text-sm text-red-400">{error}</div>
      )}

      {toast && (
        <div className="mb-4 rounded-lg bg-green-900/30 px-4 py-3 text-sm text-green-400">
          {toast}
        </div>
      )}

      {/* Tabs */}
      <div className="mb-6 flex gap-1 rounded-lg bg-gray-900 p-1 border border-gray-800 w-fit">
        <button
          onClick={() => setTab('browse')}
          className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            tab === 'browse'
              ? 'bg-gray-800 text-white'
              : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          Browse Catalogs
        </button>
        <button
          onClick={() => setTab('subscriptions')}
          className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${
            tab === 'subscriptions'
              ? 'bg-gray-800 text-white'
              : 'text-gray-400 hover:text-gray-200'
          }`}
        >
          My Subscriptions ({subscriptions.length})
        </button>
      </div>

      {tab === 'browse' && (
        <>
          {catalogs.length === 0 ? (
            <div className="rounded-lg border border-gray-800 bg-gray-900 p-12 text-center">
              <Store className="mx-auto h-12 w-12 text-gray-600" />
              <h3 className="mt-4 text-lg font-medium text-gray-300">No catalogs available</h3>
              <p className="mt-2 text-sm text-gray-500">
                There are no catalogs on the marketplace yet. Check back later.
              </p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {catalogs.map((catalog) => {
                const isMine = catalog.tenantId === user?.tenantId;
                return (
                  <div
                    key={catalog.id}
                    className="rounded-lg border border-gray-700/50 bg-gray-800/50 p-5 transition-colors hover:border-gray-600"
                  >
                    <div className="mb-3 flex items-start justify-between">
                      <div className="min-w-0 flex-1">
                        <h3 className="truncate font-medium text-white">{catalog.catalogName}</h3>
                        <p className="text-sm text-gray-400">{catalog.publisherName}</p>
                      </div>
                      <span className="ml-3 flex-shrink-0 rounded bg-gray-700 px-2 py-1 text-xs text-gray-300">
                        {catalog.productCount} products
                      </span>
                    </div>
                    {catalog.catalogDescription && (
                      <p className="mb-4 line-clamp-2 text-sm text-gray-400">
                        {catalog.catalogDescription}
                      </p>
                    )}
                    {isMine ? (
                      <button
                        disabled
                        className="w-full cursor-default rounded-lg bg-gray-700/50 py-2 text-sm text-gray-500"
                      >
                        Your catalog
                      </button>
                    ) : catalog.isSubscribed ? (
                      <button
                        disabled
                        className="w-full cursor-default rounded-lg bg-green-900/30 py-2 text-sm text-green-400"
                      >
                        Subscribed
                      </button>
                    ) : (
                      <button
                        onClick={() => handleSubscribe(catalog.id)}
                        disabled={subscribingId === catalog.id}
                        className="w-full rounded-lg bg-brand-600 py-2 text-sm text-white transition-colors hover:bg-brand-500 disabled:opacity-50"
                      >
                        {subscribingId === catalog.id ? 'Subscribing...' : 'Subscribe'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {tab === 'subscriptions' && (
        <>
          {subscriptions.length === 0 ? (
            <div className="rounded-lg border border-gray-800 bg-gray-900 p-12 text-center">
              <Store className="mx-auto h-12 w-12 text-gray-600" />
              <h3 className="mt-4 text-lg font-medium text-gray-300">No subscriptions</h3>
              <p className="mt-2 text-sm text-gray-500">
                Browse the marketplace and subscribe to catalogs to see them here.
              </p>
            </div>
          ) : (
            <div className="rounded-lg border border-gray-800 bg-gray-900">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-gray-500">
                    <th className="px-5 py-3">Catalog</th>
                    <th className="px-5 py-3">Publisher</th>
                    <th className="px-5 py-3">Subscribed</th>
                    <th className="px-5 py-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subscriptions.map((sub) => (
                    <tr key={sub.id} className="border-t border-gray-800">
                      <td className="px-5 py-3 text-sm font-medium text-gray-200">
                        {sub.catalogName}
                      </td>
                      <td className="px-5 py-3 text-sm text-gray-400">{sub.publisherName}</td>
                      <td className="px-5 py-3 text-sm text-gray-500">
                        {new Date(sub.subscribedAt).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <button
                          onClick={() => setConfirmUnsub(sub)}
                          className="text-sm text-red-400 hover:text-red-300"
                        >
                          Unsubscribe
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Unsubscribe confirmation */}
      {confirmUnsub && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-sm rounded-lg border border-gray-800 bg-gray-900 p-6">
            <h2 className="mb-2 text-lg font-semibold text-gray-100">Unsubscribe</h2>
            <p className="mb-6 text-sm text-gray-400">
              Unsubscribe from &ldquo;{confirmUnsub.catalogName}&rdquo;? This will delete the
              local catalog copy and all its products. This cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmUnsub(null)}
                className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleUnsubscribe}
                disabled={unsubscribingId === confirmUnsub.id}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {unsubscribingId === confirmUnsub.id ? 'Removing...' : 'Unsubscribe'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
